import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Button, useToast } from '@musleempay/ui';
import { useAuth } from '../../hooks/useAuth.js';
import { errorMessage } from '../../lib/api-client.js';

/**
 * Shell for the Super Admin (platform) area.
 *
 * Deliberately separate from `DashboardLayout`: that one is built around a
 * single store (its navigation, its "View storefront" link, its permissions),
 * none of which applies to someone who operates the platform. Keeping them
 * apart also means nothing platform-level can leak into a merchant's UI by way
 * of a shared navigation list.
 *
 * It is meant to grow by adding to NAV_ITEMS.
 */

const NAV_ITEMS = [
  // `end` keeps Overview from also lighting up on every page beneath /admin.
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/stores', label: 'Stores', end: false },
  { to: '/admin/plans', label: 'Plans', end: false },
  { to: '/admin/billing', label: 'Billing', end: false },
  { to: '/admin/activity', label: 'Activity', end: false },
  { to: '/admin/team', label: 'Platform team', end: false },
] as const;

export function AdminLayout(): JSX.Element {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm font-bold text-white">
              M
            </div>
            <span className="text-sm font-semibold tracking-tight text-slate-900">
              MusleemPay Platform
            </span>
          </div>

          <nav className="flex flex-1 items-center gap-1" aria-label="Platform">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'rounded-lg px-3 py-1.5 text-sm font-medium',
                    isActive
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden truncate text-sm text-slate-500 sm:inline">
              {session?.user.email}
            </span>
            <Button variant="secondary" size="sm" onClick={() => void handleLogout()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
